import { Component, OnInit, Input, ViewChild, OnDestroy, ViewEncapsulation, Renderer2 } from '@angular/core';
import {
  SpeedMonitorViewMode,
  WorkCenterDetails,
  ValuePropertyBag,
  StateTypes,
  WorkCenter,
  KPIInfoWithOrder,
  KPI,
  KPIInfo,
  KPIValueBag
} from './models/speed-monitor.model';
import { Subscription } from 'rxjs';
import { SpeedMonitorService } from './services/speed-monitor.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AvailableKpisResult,
  KpiIdentifier,
  KpiQueryMode,
  KpiValue,
  MachineData,
  MachineDowntimeData,
  MachineIdleData,
  MachineRunData,
  MachineSetupData,
  Quantity,
  WorkCenterService
} from 'chronos-core-client';
import { forEach } from 'lodash';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-speed-monitor',
  templateUrl: './speed-monitor.component.html',
  styleUrls: ['./speed-monitor.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SpeedMonitorComponent implements OnInit, OnDestroy {
  @ViewChild('SpeedMonitor') public speedMonitor;
  @Input() public mode: SpeedMonitorViewMode = SpeedMonitorViewMode.Large;
  @Input() public workCenterId: number;
  @Input() public kpisList: KPIInfoWithOrder[];
  @Input() public displayTime: number;
  public kpisListTest: KPIInfo[] = [];
  public kpisListWithOrder: KPIInfoWithOrder[] = [];
  public maxOrder: number;
  public noOfTasks: number;
  public noOfStoppers: number;
  public kpis: KPI[] = [];
  private activeSubscriptions: Subscription[] = [];

  public isRunMode = false;
  public isDowntimeMode = false;
  public isSetupMode = false;
  public isIdleMode = false;
  public isDisconnectedMode = false;
  public isProductionModeManual = false;

  public workCenter: WorkCenterDetails = {};
  public isDisplayInfo = false;
  public timeoutHandler: any;
  public timeoutHandlerOne: any;
  public valuePropertyBag: ValuePropertyBag = {};
  public kpiValueBag: KPIValueBag = {};
  public milliseconds: number;
  public deltaTime: number;
  public speed: Quantity = { value: 0, unitId: '' };
  public activeWorkcenters: WorkCenter[] = [];
  public kpiIdentifierResult: AvailableKpisResult;
  public externalWorkCenterId: string;
  public machineData: any;
  public isTvMode = false;
  public lastEventTime: Date;
  private selectedWorkCenter: WorkCenter;
  private kpiIdentifierList: string[];
  private currentIndex = 0;
  private intervalId: any;
  private readonly KPI_DISPLAY_TIME: number = 30000; // 30 seconds
  private originalMode: SpeedMonitorViewMode;

  constructor(
    private speedMonitorService: SpeedMonitorService,
    private workCenterService: WorkCenterService,
    private router: Router,
    private renderer: Renderer2,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.renderer.listen(document, 'fullscreenchange', this.onFullscreenChange.bind(this));
    this.getWorkCenterDetails();
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/Document/fullscreenchange_event
  public get isFullScreen(): boolean {
    return document.fullscreenElement ? true : false;
  }

  private onFullscreenChange(): void {
    if (!this.isFullScreen) {
      console.info('Speed monitor: Full screen mode stopped');
      this.onStopFullScreen();
    } else {
      console.info('Speed monitor: Full screen mode started');
      this.onStartFullscreen();
    }
  }

  private onStartFullscreen() {
    this.originalMode = this.mode;
    this.mode = SpeedMonitorViewMode.Large;
    this.valuePropertyBag.mode = SpeedMonitorViewMode.Large;

    this.startProcessingKPI();
  }

  private onStopFullScreen() {
    this.mode = this.originalMode;
    this.valuePropertyBag.mode = this.originalMode;

    this.startProcessingKPI();
  }

  private observeConnectionStatus() {
    // we switch to the disconnected mode if the last event time reported by the counter signal is older than 10 minutes
    if (this.lastEventTime) {
      const timeDifference = new Date().getTime() - this.lastEventTime.getTime();
      this.isDisconnectedMode = timeDifference > 10 * 60 * 1000;
    }

    setTimeout(() => this.observeConnectionStatus(), 1000);
  }

  /* Initialize the work center that is shown */
  public async getWorkCenterDetails() {
    try {
      // TV mode is a full screen mode that showns only the speed monitor of a single workcenter, no other components.
      this.isTvMode = !!this.activatedRoute.snapshot.data.isTvMode;

      // load data necessary for the the input parameter validation
      const workCenterList = await this.speedMonitorService.getWorkCenterList().toPromise();

      // validate the input: make sure that the given work center id is actually available in Chronos
      // determine the workcenter to show. If the workcenter id is provided from the route, use that one
      const externalWorkCenterIdFromRoute = this.activatedRoute.snapshot.params.externalWorkCenterId;
      this.selectedWorkCenter = this.FindWorkCenterByExternalId(workCenterList, externalWorkCenterIdFromRoute);

      // determine the workcenter to show. If it is given as parameter, use it TODO: is that field filled from external?
      if (this.externalWorkCenterId) {
        this.selectedWorkCenter = this.FindWorkCenterByExternalId(workCenterList, this.externalWorkCenterId);
      }

      // otherwise: use the last work center the user has selected
      if (!this.selectedWorkCenter && history.state.flag) {
        const workCenterId = history.state.workCenterId;
        this.selectedWorkCenter = this.FindWorkCenterById(workCenterList, workCenterId);
      }

      // otherwise: use the work center id set in this.workCenterId
      if (!this.selectedWorkCenter && this.workCenterId) {
        this.selectedWorkCenter = this.FindWorkCenterById(workCenterList, this.workCenterId);
      }

      // TODO: there is a default work center configuration in Live! Is that used anywhere?
    } catch (error) {
      console.error('Error while preparing speed monitor', error);
    }

    if (!this.selectedWorkCenter) {
      console.info('Speed monitor: No work center selected, quitting');
      return;
    }

    this.workCenterId = this.workCenterId ?? this.selectedWorkCenter.id;
    this.externalWorkCenterId = this.selectedWorkCenter.externalWorkCenterId;

    console.info(`Speed monitor: workCenterId=${this.workCenterId} externalWorkCenterId=${this.externalWorkCenterId}`);

    this.setKPIListDetails(); // prepares KPI identifiers.
    this.setMachineCounterValues();

    const activeSubscription = this.speedMonitorService.getWorkCenter(this.workCenterId).subscribe((workCenterDetails) => {
      if (workCenterDetails) {
        this.workCenter.workCenterId = workCenterDetails.externalWorkCenterId;
        this.workCenter.workCenterName = workCenterDetails.name;

        const subscription = this.speedMonitorService.getWorkCenterDetails(this.workCenterId).subscribe((workCenterData) => {
          if (workCenterData) {
            this.setWorkCenterDetails(workCenterData);
          }
        });
        this.activeSubscriptions.push(subscription);
      }
    });
    this.activeSubscriptions.push(activeSubscription);
  }

  /* Set Machine Counter Values */
  public setMachineCounterValues() {
    // subscribe to the counter changed notifications and update the UI in run mode
    const counterSubscription = this.speedMonitorService.getCounterNotifications(this.workCenterId).subscribe((result) => {
      if (result) {
        this.speed = result.speed;
        this.lastEventTime = new Date(result.eventTime);
        if (this.isRunMode) {
          this.valuePropertyBag = {
            speed: result.speed,
            targetSpeed: this.valuePropertyBag.targetSpeed,
            mode: this.mode
          };
        }
      }
    });

    this.startProcessingKPI();

    this.activeSubscriptions.push(counterSubscription);

    const stateSubscription = this.speedMonitorService.getStateNotifications(this.workCenterId).subscribe((result) => {
      if (result) {
        this.setWorkCenterDetails(result);
      }
    });
    this.activeSubscriptions.push(stateSubscription);
  }

  shouldProcessKPI() {
    return this.isTvMode || this.mode === SpeedMonitorViewMode.Large;
  }

  /* Set Value property Bag */
  public setValuePropertyBag(modewiseData?: MachineData) {
    this.isProductionModeManual = this.isDowntimeMode = this.isSetupMode = this.isIdleMode = false;
    if (this.workCenter.state) {
      switch (this.workCenter.state.toLowerCase()) {
        case StateTypes.Run.toLowerCase():
          this.setRunData(modewiseData.runData);
          break;
        case StateTypes.Downtime.toLowerCase():
          this.setDowntimeData(modewiseData.downtimeData);
          break;
        case StateTypes.Setup.toLowerCase():
          this.setSetupData(modewiseData.setupData);
          break;
        case StateTypes.Idle.toLowerCase():
          this.setIdleData();
          break;
        case StateTypes.ManualMachine.toLowerCase():
          this.isProductionModeManual = true;
          break;
      }
    }
  }

  private setRunData(runData: MachineRunData) {
    this.valuePropertyBag = {
      speed: { value: runData.speed, unitId: runData.unit },
      targetSpeed: { value: Math.ceil(runData.targetSpeed), unitId: runData.unit },
      mode: this.mode
    };
  }

  private setDowntimeData(downtimeData: MachineDowntimeData) {
    this.valuePropertyBag = {
      reasonCode: downtimeData.code,
      reasonText: downtimeData.description,
      duration: this.milliseconds,
      estimatedDuration: this.formatTimespan(downtimeData.estimatedDuration),
      mode: this.mode
    };
  }

  private setSetupData(setupData: MachineSetupData) {
    this.valuePropertyBag = {
      duration: this.milliseconds,
      estimatedDuration: this.formatTimespan(setupData.estimatedDuration),
      mode: this.mode
    };
  }

  private setIdleData() {
    this.valuePropertyBag = {
      duration: this.milliseconds,
      mode: this.mode
    };
  }

  /* Set WorkCenter Details */
  public setWorkCenterDetails(workCenterData: MachineData) {
    this.workCenter.state = workCenterData.state;
    this.workCenter.serverUtcTime = workCenterData.serverUtcTime;
    this.lastEventTime = new Date(workCenterData.lastEventTime);
    const requestTimeBrowser = this.speedMonitorService.requestTimeBrowser;
    const deltaTime = this.speedMonitorService.deltaTimeCalc(requestTimeBrowser, this.workCenter.serverUtcTime);
    this.deltaTime = deltaTime > 0 ? deltaTime : 0;
    if (workCenterData.infoData && this.mode === SpeedMonitorViewMode.Large) {
      this.workCenter.infoData = {
        message: workCenterData.infoData.message,
        displayDurationSeconds: workCenterData.infoData.displayDurationSeconds || 0,
        displayIntervalSeconds: workCenterData.infoData.displayIntervalSeconds || 0
      };
      this.setDisplayInformation(this.workCenter.infoData);
      this.speedMonitorService.workCenterDetails.next(this.workCenter);
    }
    if (this.workCenter.state) {
      this.isProductionModeManual = this.isRunMode = this.isDowntimeMode = this.isSetupMode = this.isIdleMode = false;
      switch (this.workCenter.state.toLowerCase()) {
        case StateTypes.Run.toLowerCase():
          this.isRunMode = true;
          this.setRunData(workCenterData.runData);
          break;
        case StateTypes.Downtime.toLowerCase():
          this.isDowntimeMode = true;
          this.setDowntimeModeWorkCenterDetails(workCenterData.downtimeData);
          break;
        case StateTypes.Setup.toLowerCase():
          this.isSetupMode = true;
          this.setSetupModeWorkCenterDetails(workCenterData.setupData);
          break;
        case StateTypes.Idle.toLowerCase():
          this.isIdleMode = true;
          this.setIdleModeWorkCenterDetails(workCenterData.idleData);
          break;
        case StateTypes.ManualMachine.toLowerCase():
          this.isProductionModeManual = true;
          break;
        default:
          console.info(`Speed monitor: Unknown state from backend: ${this.workCenter.state}`);
      }

      this.observeConnectionStatus();
    }
  }

  public setDisplayInformation(displayInfoData: any): void {
    if (displayInfoData && displayInfoData.message && displayInfoData.displayDurationSeconds > 0) {
      this.timeoutHandler = setTimeout(() => {
        this.isDisplayInfo = true;
        this.timeoutHandlerOne = setTimeout(() => {
          this.isDisplayInfo = false;
          this.setDisplayInformation(displayInfoData);
        }, displayInfoData.displayDurationSeconds * 1000);
      }, displayInfoData.displayIntervalSeconds * 1000);
    } else {
      this.isDisplayInfo = false;
    }
  }

  /* Set Run Mode Data */
  public setKPIListDetails(): void {
    if (this.kpisList === null || this.kpisList === undefined) {
      this.prepareKpiListObject();
    }
    this.prepareKpiIdentifiers();
  }

  public async startProcessingKPI() {
    if (!this.shouldProcessKPI()) {
      this.stopProcessingKPI();
      return;
    }

    this.stopProcessingKPI();

    await this.getKpiValues();
    this.moveToNextKpi();

    this.intervalId = setInterval(async () => {
      await this.getKpiValues();
      this.moveToNextKpi();
    }, this.displayTime ?? this.KPI_DISPLAY_TIME);
  }

  private moveToNextKpi() {
    this.currentIndex++;
    if (this.currentIndex >= this.kpiIdentifierList.length) {
      this.currentIndex = 0; // Reset the interval when all values have been processed.
    }
  }

  private stopProcessingKPI() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public getListofKpis(kpilisttest: KPIInfoWithOrder[]) {
    this.maxOrder = Math.max(...kpilisttest.map((o) => o.order ?? -1));
    this.kpisList = kpilisttest.map((kpi) => {
      kpi.hidden = false;
      kpi.order = kpi.order ?? ++this.maxOrder;
      return kpi;
    });
  }

  public prepareKpiListObject() {
    let i = 0;
    Object.keys(KpiIdentifier).forEach((key) => {
      if (key !== 'values') {
        this.kpisListTest[i] = { id: key, name: KpiIdentifier[key] };
        i++;
      }
    });
    this.getListofKpis(this.kpisListTest);
  }

  // returns list of KPI's
  public prepareKpiIdentifiers() {
    const kpiIdentifiers: string[] = [];
    if (this.kpisList) {
      const kpiNames = this.kpisList.map((kpi) => {
        if (kpi.id === Object.keys(KpiIdentifier).find((key) => kpi.id === key && key !== 'values')) {
          return kpi.name;
        }
      });
      forEach(kpiNames, (kpiName) => {
        kpiIdentifiers.push(kpiName);
      });
    }

    this.kpiIdentifierList = kpiIdentifiers;
  }

  /* KPI endpoint call */
  public getKpiValues(): Promise<KpiValue[]> {
    const kpiIdentifiers = [...this.kpiIdentifierList.slice(this.currentIndex), ...this.kpiIdentifierList.slice(0, this.currentIndex)];

    console.info(`Loading KPIs for WorkCenter=${this.workCenterId}, KPI Identifiers=${kpiIdentifiers}`);
    const task = this.workCenterService.getKpis({
      workCenterId: this.workCenterId,
      body: {
        queryMode: KpiQueryMode.First,
        kpiIdentifiers: kpiIdentifiers as KpiIdentifier[]
      }
    });
    task.subscribe((result) => {
      if (result && result.length > 0) {
        this.kpis = [];
        result.forEach((kpivalue) => {
          const kpi = this.kpisList.find((kpiInfo) => kpivalue.kpiIdentifier === kpiInfo.name);
          const kpiIndex = this.kpisList.findIndex((kpiInfo) => kpivalue.kpiIdentifier === kpiInfo.name);
          if (kpi) {
            this.kpis.push({
              id: kpi.id,
              kpiIdentifier: kpivalue.kpiIdentifier,
              name: this.translateService.instant(`KPI_TILE.${kpivalue.kpiIdentifier.toUpperCase()}`),
              value: kpivalue.value,
              valueTime: this.formatTimespan(kpivalue.valueTime),
              order: kpi.order
            });
          }

          if (kpiIndex >= 0) {
            this.currentIndex = kpiIndex;
          }
        });

        this.kpiValueBag = {
          kpiValue: this.kpis
        };
      }
    });

    return task.toPromise();
  }

  /* Set Downtime Mode Data */
  public setDowntimeModeWorkCenterDetails(downtimeModeData: MachineDowntimeData): void {
    this.isDowntimeMode = true;
    this.isRunMode = false;
    this.isSetupMode = false;
    this.isIdleMode = false;
    this.isProductionModeManual = false;

    this.machineData = downtimeModeData;
    this.milliseconds = this.speedMonitorService.getDuration(this.machineData.startTime, this.deltaTime);
    this.setDowntimeData(downtimeModeData);
  }

  /* Set Setup Mode Data */
  public setSetupModeWorkCenterDetails(setupModeData: MachineSetupData): void {
    this.isSetupMode = true;
    this.isRunMode = false;
    this.isDowntimeMode = false;
    this.isIdleMode = false;
    this.isProductionModeManual = false;

    this.machineData = setupModeData;
    this.milliseconds = this.speedMonitorService.getDuration(this.machineData.startTime, this.deltaTime);
    this.setSetupData(setupModeData);
  }

  /* Set Idle Mode Data */
  public setIdleModeWorkCenterDetails(idleModeData: MachineIdleData): void {
    this.isIdleMode = true;
    this.isSetupMode = false;
    this.isRunMode = false;
    this.isDowntimeMode = false;

    this.machineData = idleModeData;
    this.milliseconds = this.speedMonitorService.getDuration(this.machineData.startTime, this.deltaTime);
    this.setIdleData();
  }

  public redirectToFullscreen() {
    this.router.navigateByUrl(`/speedmonitor/${this.externalWorkCenterId}`, {
      state: {
        workCenterId: this.workCenterId,
        flag: true,
        isFullViewMode: true,
        kpiList: this.kpiValueBag.kpiValue,
        displayTime: this.displayTime
      }
    });
  }

  public fullscreen(): void {
    const element = this.speedMonitor.nativeElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen();
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen();
    }

    // Note: the full screen changed event handler has the processing for starting and stopping the event loop
  }

  public ngOnDestroy() {
    this.activeSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    clearTimeout(this.timeoutHandler);
    clearTimeout(this.timeoutHandlerOne);
    this.stopProcessingKPI();
  }

  private formatTimespan(timeStamp: string): string {
    if (!timeStamp) {
      return null;
    }
    const timeStampParts = timeStamp.split('.');

    // we need to determine if the first part contains the hourse / minutes / seconds pattern. If not it is the days
    let timeStampWithoutMilliseconds;
    let days = null;

    if (timeStampParts[0].includes(':')) {
      timeStampWithoutMilliseconds = timeStampParts[0];
    } else if (timeStampParts.length > 1 && timeStampParts[1].includes(':')) {
      days = timeStampParts[0];
      timeStampWithoutMilliseconds = timeStampParts[1];
    } else {
      return null;
    }

    const numbers = timeStampWithoutMilliseconds.split(':');
    const hours = numbers[0];
    const minutes = numbers[1];
    const seconds = Math.ceil(parseInt(numbers[2], 10)).toString().padStart(2, '0');
    return `${days ? `${days}.` : ''}${hours}:${minutes}:${seconds}`;
  }

  private FindWorkCenterByExternalId(workCenters: WorkCenter[], externalWorkCenterId: string): WorkCenter {
    if (!externalWorkCenterId) {
      return null;
    }
    const filteredWorkCenters = (this.activeWorkcenters = workCenters.filter((wc) => wc.externalWorkCenterId === externalWorkCenterId));
    if (filteredWorkCenters.length === 0) {
      return null;
    } else {
      return filteredWorkCenters[0];
    }
  }

  private FindWorkCenterById(workCenters: WorkCenter[], workCenterId: number): WorkCenter {
    if (!workCenterId) {
      return null;
    }
    const filteredWorkCenters = (this.activeWorkcenters = workCenters.filter((wc) => wc.id === workCenterId));
    if (filteredWorkCenters.length === 0) {
      return null;
    } else {
      return filteredWorkCenters[0];
    }
  }
}
